import type { Game } from '../types/Game'
import type { JackpotGameMap } from '../types/composables/UseJackpot'
import { isEmpty } from 'es-toolkit/compat'
import { computed, toValue, useGameConfig, useGameType } from '#imports'
import type { MaybeRefOrGetter } from '#imports'

/**
 * Derives the jackpot display values for a single game from a (possibly shared)
 * jackpots map. Shared by {@link useGameTile} (list-scoped query) and
 * {@link useGameJackpot} (standalone query) so the derivation lives in one place.
 *
 * @param game - The game to resolve values for.
 * @param gamesJackpots - The slug→jackpot map (e.g. `useGamesJackpots().gamesJackpots`).
 */
export const useGameJackpotBindings = (
    game: MaybeRefOrGetter<Game | undefined>,
    gamesJackpots: MaybeRefOrGetter<Record<string, JackpotGameMap> | undefined>,
) => {
    const { isBingo, isSlot, isCollector } = useGameType()
    const { bingo, translations } = useGameConfig()

    const jackpotMapLoaded = computed(() => !isEmpty(toValue(gamesJackpots)))

    // Reads one jackpot field for the current game from the shared map (0 if absent).
    function jackpotValue(field: keyof JackpotGameMap): number {
        const current = toValue(game)
        if (!current) return 0
        return Number(toValue(gamesJackpots)?.[current.slug]?.[field] ?? 0)
    }

    // Slot/collector games resolve their jackpot values from the shared map;
    // bingo is handled separately from config.
    const isSlotOrCollector = computed(() => {
        const current = toValue(game)
        return !!current && (isSlot(current.game_type) || isCollector(current.game_type))
    })

    const isBingoSlot = computed(() => {
        const current = toValue(game)
        if (!current || !jackpotMapLoaded.value) return false
        return isSlot(current.game_type) && jackpotValue('megaprize') > 0
    })

    const jackpot = computed<number | null>(() => {
        const current = toValue(game)
        if (!current) return null
        if (isBingo(current.game_type)) return Number(bingo.mega_prize)
        if (!jackpotMapLoaded.value) return null
        return isSlotOrCollector.value ? jackpotValue('jackpot') : null
    })

    const superJackpot = computed<number | null>(() => {
        if (!toValue(game)) return null
        if (!jackpotMapLoaded.value) return null
        return isSlotOrCollector.value ? jackpotValue('superJackpot') : null
    })

    const megaprize = computed<number | null>(() => {
        const current = toValue(game)
        if (!current) return null
        if (isBingo(current.game_type)) return Number(bingo.mega_prize)
        if (!jackpotMapLoaded.value) return null
        return isBingoSlot.value ? jackpotValue('megaprize') : null
    })

    const jackpotTypeText = computed<string | null>(() => {
        const current = toValue(game)
        if (!current) return null
        if (isBingo(current.game_type)) return translations.gameTile.mega_prize
        if (!jackpotMapLoaded.value) return null
        if (isBingoSlot.value) return translations.gameTile.mega_prize
        return isSlotOrCollector.value ? translations.gameTile.super_jackpot : null
    })

    const jackpotsLoaded = computed(() => {
        const current = toValue(game)
        if (!current) return false
        if (isBingo(current.game_type)) return true
        return jackpotMapLoaded.value
    })

    return {
        jackpot,
        superJackpot,
        megaprize,
        jackpotTypeText,
        isBingoSlot,
        jackpotsLoaded,
    }
}
